import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { CheckCircle, XCircle, Play, Eye, ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_COLOR = {
  pending: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  approved: "text-[#00ff77] bg-[#00ff77]/10 border-[#00ff77]/20",
  rejected: "text-red-400 bg-red-400/10 border-red-400/20",
};

export default function AdminContent() {
  const [content, setContent] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadContent(); }, []);

  const loadContent = async () => {
    try {
      // No orderBy — legacy docs without createdAt are excluded by Firestore orderBy
      const snap = await getDocs(collection(db, "content"));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setContent(items);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, "content", id), { status });
    setContent((c) => c.map((x) => x.id === id ? { ...x, status } : x));
  };

  const filtered = content.filter((c) => filter === "all" || c.status === filter);

  return (
    <div data-testid="admin-content" className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/admin" className="text-zinc-500 hover:text-white text-sm flex items-center gap-1"><ChevronLeft size={16} /> Admin</Link>
        <h2 className="text-xl font-black text-white">Content Review</h2>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${filter === s ? "bg-[#00ff77]/20 text-[#00ff77] border border-[#00ff77]/30" : "bg-[#111] border border-white/10 text-zinc-400 hover:border-white/20"}`}>
            {s}
            {s === "pending" && <span className="ml-1.5 bg-yellow-400/20 text-yellow-400 text-[10px] px-1.5 rounded-full">{content.filter((c) => c.status === "pending").length}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <p className="text-zinc-500 text-sm py-8 text-center">No content with status: {filter}</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((item) => (
            <div key={item.id} data-testid={`content-row-${item.id}`}
              className="flex items-center gap-3 bg-[#111] border border-white/5 rounded-xl p-3">
              <div className="w-16 h-11 rounded-lg bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                {item.thumbnailURL ? (
                  <img src={item.thumbnailURL} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Play size={12} className="text-zinc-600" /></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">{item.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border capitalize ${STATUS_COLOR[item.status] || STATUS_COLOR.pending}`}>{item.status}</span>
                  <span className="text-zinc-600 text-xs">{item.creatorName}</span>
                  <span className="text-zinc-600 text-xs bg-white/5 px-1.5 py-0.5 rounded-full">{item.category}</span>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Link to={`/content/${item.id}`} className="p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors">
                  <Eye size={14} />
                </Link>
                {item.status !== "approved" && (
                  <button onClick={() => updateStatus(item.id, "approved")} data-testid={`approve-content-${item.id}`}
                    className="flex items-center gap-1 text-[10px] bg-[#00ff77]/10 border border-[#00ff77]/20 text-[#00ff77] px-2 py-1 rounded-lg hover:bg-[#00ff77]/20 transition-colors">
                    <CheckCircle size={11} /> OK
                  </button>
                )}
                {item.status !== "rejected" && (
                  <button onClick={() => updateStatus(item.id, "rejected")} data-testid={`reject-content-${item.id}`}
                    className="flex items-center gap-1 text-[10px] bg-red-500/10 border border-red-500/20 text-red-400 px-2 py-1 rounded-lg hover:bg-red-500/20 transition-colors">
                    <XCircle size={11} /> Reject
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
